import React, { useCallback, useEffect, useMemo } from 'react'
import T from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Box, Typography, Divider, Stack } from '@mui/material'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { useParams } from 'react-router-dom'
import config from 'config'
import { COUNTRY_CONFIG_MAP } from 'lib/countryConfig'

import Table from 'components/atoms/Table'
import DropArea from 'components/atoms/DropArea'
import ConfirmBar from 'components/atoms/ConfirmBar'
import DownloadBar from 'components/atoms/DownloadBar'
import TableHeaderCell from 'components/atoms/TableHeaderCell'
import FilterSelect from 'components/atoms/FilterSelect'
import SearchField from 'components/atoms/SearchField'
import TitleBar from 'components/atoms/TitleBar'
import UploadBar from 'components/atoms/UploadBar'

import useCarsFilters from 'hooks/useCarsFilters'
import useInputOutput, {
  BASE_FILE_UPLOAD_EXTENSIONS,
  validateExpectedLabel,
  validateNotEmpty,
} from 'hooks/useInputOutput'

import SessionsSearchBar from '../Sessions/SessionsSearchBar'

const createColumns = (t, countryConfig, carsFilters) => {
  const {
    filters,
    filtersData,
    setBrands,
    setModels,
    setPlateQuery,
    setVinQuery,
  } = carsFilters

  return [
    {
      accessor: 'brand',
      Header: (
        <TableHeaderCell
          label={t('sessions.pricing.table.brand')}
          filter={
            <FilterSelect
              data-testid="search-brand"
              options={filtersData.brands}
              value={filters.brands}
              handleChange={setBrands}
              multiple
              hasSearchField
              searchLabel={t('common.form.searchCarBrand')}
            />
          }
        />
      ),
      sticky: 'left',
    },
    {
      accessor: 'model',
      Header: (
        <TableHeaderCell
          label={t('sessions.pricing.table.model')}
          filter={
            <FilterSelect
              data-testid="search-models"
              options={filtersData.models}
              value={filters.models}
              handleChange={setModels}
              multiple
              hasSearchField
              searchLabel={t('common.form.searchCarModel')}
            />
          }
        />
      ),
      sticky: 'left',
    },
    ...(countryConfig.tableShow.vin
      ? [
          {
            accessor: 'vin',
            Header: (
              <TableHeaderCell
                label={t('sessions.pricing.table.vin')}
                filter={
                  <SearchField
                    data-testid="search-vin"
                    placeholder={t('common.form.searchCarVin')}
                    value={filters.vinQuery}
                    onChange={(event) => setVinQuery(event.target.value)}
                    fullWidth={false}
                  />
                }
              />
            ),
            sticky: 'left',
            width: 190,
          },
        ]
      : []),
    ...(countryConfig.tableShow.plate
      ? [
          {
            accessor: 'plate',
            Header: (
              <TableHeaderCell
                label={t('sessions.pricing.table.plate')}
                filter={
                  <SearchField
                    data-testid="search-plate"
                    placeholder={t('common.form.searchCarPlate')}
                    value={filters.plateQuery}
                    onChange={(event) => setPlateQuery(event.target.value)}
                    fullWidth={false}
                  />
                }
              />
            ),
            sticky: 'left',
            width: 190,
          },
        ]
      : []),
    {
      accessor: 'suggestedPrice',
      Header: (
        <TableHeaderCell label={t('sessions.pricing.table.suggestedPrice')} />
      ),
      width: 92,
    },
    {
      accessor: 'fee',
      Header: <TableHeaderCell label={t('sessions.pricing.table.fee')} />,
      width: 55,
    },
    {
      accessor: 'group1',
      Header: (
        <TableHeaderCell
          // eslint-disable-next-line sonarjs/no-duplicate-string
          label={t('sessions.pricing.table.sixtPrice')}
        />
      ),
      width: 92,
    },
  ]
}

export default function SessionsCarsPricing({
  session,
  stepCars,
  update,
  handleValidation,
  handleConfirm,
  fetchData,
  confirmed,
  readOnly,
  contentIsReadOnly = false,
}) {
  const { t } = useTranslation()

  const countryConfig = COUNTRY_CONFIG_MAP[config.country ?? 'it']

  const { step } = useParams()

  const groupsTotal = useMemo(
    () => session.requiredGroups.filter((group) => group).length,
    [session.requiredGroups]
  )

  const carsFilters = useCarsFilters()
  const { filters, initFilters, setQuery, filterCars, resetFilters } =
    carsFilters

  useEffect(() => {
    if (!stepCars.length) {
      return
    }

    initFilters(stepCars)
  }, [stepCars, initFilters])

  const columns = useMemo(
    () => createColumns(t, countryConfig, carsFilters),
    [t, carsFilters, countryConfig]
  )

  const uploaded = useMemo(() => stepCars.length > 0, [stepCars])

  const validateNumber = (value, row, column) => {
    if (value !== 0 && (!value || isNaN(value))) {
      return false
    }
    return true
  }

  const parser = (rows) => {
    const wrongColumnErrorPath = 'common.error.fileUpload.wrongColumnName'
    validateExpectedLabel(
      rows,
      1,
      1,
      t(`common.excel.${countryConfig.attribute.primary}`),
      wrongColumnErrorPath,
      t
    )

    validateExpectedLabel(
      rows,
      1,
      2,
      t('common.excel.sixtPrice'),
      wrongColumnErrorPath,
      t
    )

    validateExpectedLabel(
      rows,
      1,
      3,
      t('common.excel.fee'),
      wrongColumnErrorPath,
      t
    )

    return rows
      .slice(2)
      .map((row, i) =>
        row.length && validateNotEmpty(rows, i + 2, 1)
          ? {
              [countryConfig.attribute.primary]: row[1]
                .toString()
                .trim()
                .toUpperCase(),
              prices: [
                null,
                (validateNumber(row[2]) && Number(row[2])) || null,
              ],
              price: (validateNumber(row[2]) && Number(row[2])) || null,
              fee: (validateNumber(row[3]) && Number(row[3])) || null,
            }
          : null
      )
      .filter((item) => item)
  }

  const handleFetchData = useCallback(() => {
    fetchData(session?.sessionId)
    resetFilters()
  }, [fetchData, resetFilters, session?.sessionId])

  const handleUpdate = useCallback(
    (cars, fileName) => {
      update(session?.sessionId, cars, fileName)
      resetFilters()
    },
    [resetFilters, session?.sessionId, update]
  )

  const { handleDrop, handleSelectFile, saveXls } = useInputOutput(
    handleUpdate,
    parser
  )

  const handleDownload = async () => {
    const titlesRow = [
      t(`common.excel.${countryConfig.attribute.primary}`),
      t('common.excel.sixtPrice'),
      t('common.excel.fee'),
    ]
    titlesRow.push(t('common.excel.name'))

    const cars = uploaded ? preparedItems : []
    const rows = [
      titlesRow,
      ...cars.map((car) => [
        car[countryConfig.attribute.primary],
        car.prices?.[1],
        car.fee || '-',
        `${car.title}`,
      ]),
    ]
    saveXls('prices', rows)
  }

  const preparedItems = useMemo(() => {
    const numberFormatter = new Intl.NumberFormat('it-IT')

    return stepCars.map((car) => {
      return {
        ...car,
        reservePrice: car.prices[0] && numberFormatter.format(car.prices[0]),
        fee: car.fee && numberFormatter.format(car.fee),
        suggestedPrice:
          car.prices[1] || car.fee
            ? numberFormatter.format((car.prices[1] || 0) + (car.fee || 0))
            : null,
        ...session.requiredGroups
          .filter((group) => group)
          .reduce(
            (previous, current) => ({
              ...previous,
              [`group${current}`]:
                car.prices[current] &&
                numberFormatter.format(car.prices[current]),
            }),
            {}
          ),
      }
    })
  }, [stepCars, session.requiredGroups])

  const filteredCars = useMemo(
    () => filterCars(preparedItems, filters),
    [preparedItems, filterCars, filters]
  )

  return (
    <div data-testid="session-pricing">
      <TitleBar
        title={t(`sessions.pricing.contentTitle`)}
        description={t(`sessions.pricing.contentDescription`)}
        rightColumn={
          <Stack direction="row" alignItems="center">
            <UploadBar
              id={step}
              fileName={session?.[`${step}FileName`]}
              readOnly={readOnly || contentIsReadOnly}
              uploaded={uploaded}
              handleEmptyList={() => handleUpdate(session.sessionId, [])}
              handleSelectFile={handleSelectFile}
              handleFetchData={handleFetchData}
            />
          </Stack>
        }
      />
      {uploaded ? (
        <>
          <Table
            id="cars-list"
            items={filteredCars}
            columns={columns}
            top={
              <>
                <ConfirmBar
                  component="div"
                  label={t(`sessions.pricing.confirmButton`)}
                  left={
                    <>
                      <SessionsSearchBar
                        id="text"
                        label={t('sessions.pricing.carsUploaded')}
                        filteredLabel={t('common.filters.filteredCars')}
                        cars={uploaded ? stepCars : []}
                        filteredCars={filteredCars}
                        query={filters.query}
                        handleChangeSearch={(event) =>
                          setQuery(event.target.value)
                        }
                      />
                    </>
                  }
                  leftProps={{ sx: { width: 0.5 } }}
                  right={
                    <>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        flex="1"
                      >
                        {groupsTotal < session.requiredGroups.length && (
                          <Stack direction="row" alignItems="center">
                            <Box pr={4} />
                            <WarningAmberRoundedIcon
                              color="secondary"
                              sx={{ bgcolor: 'error.main', borderRadius: 1 }}
                            />
                            <Box pr={1} />
                            <Typography variant="body2" color="error">
                              {t('sessions.pricing.partnerNotAssigned')}
                            </Typography>
                          </Stack>
                        )}
                        <DownloadBar
                          readOnly={readOnly || contentIsReadOnly}
                          handleDownload={handleDownload}
                        />
                      </Stack>
                    </>
                  }
                  rightProps={{
                    sx: {
                      width:
                        groupsTotal < session.requiredGroups.length
                          ? 0.5
                          : undefined,
                      justifyContent: 'space-between',
                    },
                  }}
                  disabled={
                    !handleValidation() ||
                    confirmed ||
                    readOnly ||
                    contentIsReadOnly
                  }
                  handleConfirm={handleConfirm}
                />
                <Divider />
              </>
            }
          />
        </>
      ) : (
        <>
          <DropArea
            onDrop={handleDrop}
            disabled={readOnly || contentIsReadOnly}
            accept={BASE_FILE_UPLOAD_EXTENSIONS.toString()}
          />
        </>
      )}
    </div>
  )
}

SessionsCarsPricing.propTypes = {
  session: T.object.isRequired,
  stepCars: T.array.isRequired,
  update: T.func.isRequired,
  handleValidation: T.func.isRequired,
  handleConfirm: T.func.isRequired,
  fetchData: T.func.isRequired,
  confirmed: T.bool,
  readOnly: T.bool,
  contentIsReadOnly: T.bool,
}
